import React from "react";
import { Icon, notify, confirmDialog, DataTable, useLocalPref, useNarrowScreen } from "./registry";
import { CATS, CAT_COLOR } from "./producteditor";
import { csvRecords, toCsv, downloadCsv } from "./lib/csv";
import { newId } from "./lib/ids";
import { localYmd } from "./lib/dates";
import { can, roleOf } from "./lib/roles";
import { sampleJob } from "./lib/samplejob";
import {
  IMPORT_MAX_BYTES, IMPORT_MAX_ROWS, PREVIEW_ROWS, UNDO_SLICE, UNDO_SLICE_PRODUCTS, PRICEBOOK_MAX, STAGE_LABEL, SOURCE_LABELS,
  fieldsFor, autoMap, planCustomers, planJobs, planProducts, previewRows, batchFromPlan, importLeft, templateRows,
  customersExport, quotesExport, jobsExport, productsExport, round2,
} from "./lib/dataio";
import { sampleCustomersCsv, sampleJobsCsv } from "./dataio/samples";
/* ============================================================
   Fence Works — Import & export
   How a contractor switching from Jobber, Housecall Pro, QuickBooks or a
   spreadsheet brings their book in themselves: drop the CSV, check the
   column matches, read the preview, import — and Undo if it looks wrong.
   The other half takes every list back out as CSV (the Free plan's "lead
   list with CSV export" lives here and on Customers / Estimates).
   The decisions (matching, dedupe, what a row becomes, undo) are pure
   functions in lib/dataio.ts; this file is the screen around them.
   ============================================================ */
const DIO_money = window.VFP.money, DIO_money0 = window.VFP.money0;
const DEMO_ON = () => !!((window as any).VFP_DEMO && (window as any).VFP_DEMO.active);
const KIND_WORD: Record<string, [string, string]> = { customers: ["customer", "customers"], jobs: ["job", "jobs"], products: ["product", "products"] };
const plural = (n: number, kind: string) => (Number(n) || 0).toLocaleString("en-US") + " " + (KIND_WORD[kind] || ["record", "records"])[n === 1 ? 0 : 1];

/* ---- export: every list as a CSV, from here, Customers and Estimates ---- */
function downloadExport(kind: string, store: any): number {
  const role = roleOf(store);
  let t: any;
  if (kind === "customers") t = customersExport(store);
  else if (kind === "estimates") t = quotesExport(store);
  else if (kind === "jobs") t = jobsExport(store, { costs: can(role, "money.cost") });
  else t = productsExport(window.VFP.PRODUCTS || [], { perFtOf: (p: any) => sampleJob(p, store.settings).perFoot });
  downloadCsv(`fenceworks-${kind}-${localYmd()}.csv`, toCsv(t.columns, t.rows));
  notify(`Downloaded ${t.rows.length} ${kind === "estimates" ? (t.rows.length === 1 ? "estimate" : "estimates") : (KIND_WORD[kind] || ["row", "rows"])[t.rows.length === 1 ? 0 : 1]} as CSV`, { kind: "success" });
  return t.rows.length;
}

/* a CSV off the disk. Excel on Windows still saves "CSV" as Windows-1252 —
   if the bytes aren't valid UTF-8, read them that way so "Muñoz" survives. */
async function readFileText(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  let text = new TextDecoder("utf-8").decode(buf);
  if (text.indexOf("�") > -1) { try { text = new TextDecoder("windows-1252").decode(buf); } catch (e) { /* keep the UTF-8 read */ } }
  return text;
}

/* is this tab writing to the cloud? (the sandbox and local-only mode aren't) */
function cloudOn(): boolean {
  const DB: any = window.VFP_DB;
  return !!(DB && DB.ready && !DEMO_ON());
}
/* wait for the save that carries the change just made: a "saved" only counts
   after a "saving" (a save already in flight when we started isn't ours) */
function waitForSave(ms = 90000): Promise<string> {
  const DB: any = window.VFP_DB;
  return new Promise((resolve) => {
    let off: any = null, sawSaving = false, done = false, grace: any = null;
    const finish = (r: string) => { if (done) return; done = true; clearTimeout(timer); clearTimeout(grace); try { off && off(); } catch (e) {} resolve(r); };
    const timer = setTimeout(() => finish("timeout"), ms);
    off = DB.onStatus((st: string) => {
      if (st === "saving") sawSaving = true;
      else if (st === "saved" && sawSaving) finish("saved");
      else if (st === "stale" || st === "guard" || st === "nosession") finish(st);
      // an optional table (AI settings) can report an error mid-save and the core save still land
      else if (st === "error" && sawSaving && !grace) grace = setTimeout(() => finish("error"), 2500);
    });
    try { DB.flushNow && setTimeout(() => DB.flushNow(), 1000); } catch (e) {}
  });
}
const tick = (ms = 60) => new Promise((r) => setTimeout(r, ms));

/* Undo, shared by the result card and the history list. In the cloud the
   records go a slice at a time, each slice waiting for its save — the save's
   delete filter rides the URL, and a few hundred ids at once is past what the
   gateway accepts. Locally (and in the sandbox) it's one step. */
function useUndo(store: any, sync: any) {
  const [busy, setBusy] = React.useState<any>(null);
  const storeRef = React.useRef<any>(store); storeRef.current = store;
  async function undo(entry: any) {
    if (busy) return;
    const left = importLeft(storeRef.current, entry.id);
    if (!left.total) { notify("Nothing from that import is left to undo."); return; }
    if (sync === "error") { notify("The cloud can't be reached, so this tab is read-only — undo when the dot at the top is green again.", { kind: "error" }); return; }
    const parts = [
      left.jobs ? plural(left.jobs, "jobs") : "", left.customers ? plural(left.customers, "customers") : "", left.products ? plural(left.products, "products") : "",
    ].filter(Boolean);
    const list = parts.length > 1 ? parts.slice(0, -1).join(", ") + " and " + parts[parts.length - 1] : parts[0];
    const body = [
      parts.length ? `Removes the ${list} it added — including anything you've changed on them since.` : "",
      left.fills ? `Clears what it filled in on ${left.fills} existing record${left.fills === 1 ? "" : "s"}, unless you've edited that field since.` : "",
      left.linked ? `${left.linked} estimate${left.linked === 1 ? "" : "s"} or job${left.linked === 1 ? "" : "s"} from elsewhere point at those customers — they stay, unlinked, with the name kept.` : "",
    ].filter(Boolean).join("\n");
    const ok = await confirmDialog({ title: `Undo the ${entry.file || "import"} import?`, body, confirmLabel: "Undo import", cancelLabel: "Keep it", danger: true });
    if (!ok) return;
    const sliced = cloudOn();
    const total = left.created || 1;
    setBusy({ id: entry.id, done: 0, total });
    try {
      for (let guard = 0; guard < 400; guard++) {
        if (!importLeft(storeRef.current, entry.id).total) break;
        const saved = sliced ? waitForSave() : null;
        storeRef.current.undoImport(entry.id, sliced ? { max: UNDO_SLICE, maxProducts: UNDO_SLICE_PRODUCTS } : undefined);
        await tick();
        if (saved) {
          const r = await saved;
          if (r !== "saved") {
            notify(r === "stale" ? "This tab was behind the cloud, so the undo paused and the latest data was loaded. Press Undo again to finish."
              : "Undo paused — the last part didn't reach the cloud. Press Undo again to pick up where it stopped.", { kind: "error", ms: 10000 });
            return;
          }
        }
        const now = importLeft(storeRef.current, entry.id);
        setBusy({ id: entry.id, done: Math.max(0, total - now.created), total });
        if (!sliced) break;
      }
      notify(`Undid the ${entry.file || "import"} import`, { kind: "success" });
    } finally { setBusy(null); }
  }
  return { undo, busy };
}

/* ---- the screen ---- */
function DataIOPage({ store, go, params, sync }: any) {
  const role = roleOf(store);
  const canPrice = can(role, "pricebook"), canJobs = can(role, "jobs"), canEst = can(role, "estimates");
  const TABS = [
    { id: "customers", label: "Import customers", icon: "user" },
    canJobs && { id: "jobs", label: "Import jobs", icon: "clipboard" },
    canPrice && { id: "products", label: "Import price book", icon: "box" },
    { id: "export", label: "Export", icon: "download" },
  ].filter(Boolean) as any[];
  const [pref, setPref] = useLocalPref("fw_dataio_tab", "customers");
  const want = (params && params.tab) || pref;
  const tab = TABS.some((t) => t.id === want) ? want : "customers";
  const { undo, busy } = useUndo(store, sync);

  return (
    <div className="page fade-up" style={{ maxWidth: 1180 }}>
      <div className="row between" style={{ marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div><h1 className="display" style={{ fontSize: 28, margin: 0 }}>Import &amp; export</h1>
          <p className="muted" style={{ margin: "4px 0 0", maxWidth: 680 }}>Bring your customers, jobs and price book in from a spreadsheet, Jobber, Housecall Pro, QuickBooks or Google Contacts — and take any list back out as a CSV.</p></div>
      </div>
      <div className="seg wrap" role="tablist" aria-label="Import or export" style={{ marginBottom: 18 }}>
        {TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} className={"seg-btn" + (tab === t.id ? " on" : "")}
            onClick={() => { setPref(t.id); if (params && params.tab) go("dataio"); }}>
            <Icon name={t.icon} size={13} style={{ marginRight: 6, verticalAlign: -2 }} />{t.label}
          </button>
        ))}
      </div>

      {tab === "export"
        ? <ExportPanel store={store} canPrice={canPrice} canJobs={canJobs} canEst={canEst} />
        : <ImportPanel key={tab} kind={tab} store={store} go={go} sync={sync} undo={undo} busy={busy} />}

      <HistoryCard store={store} undo={undo} busy={busy} />
    </div>
  );
}

const HELP: Record<string, string[]> = {
  customers: [
    "Jobber, Housecall Pro, QuickBooks: export the client or customer list. If it comes out as an Excel file, open it and save it as CSV.",
    "Google Contacts: Export → Google CSV.",
    "A spreadsheet works too — one row per customer, the column names in the first row.",
  ],
  jobs: [
    "Your job or invoice history as CSV — one row per job: customer, job, status, dates and the total. Costs and hours are optional.",
    "Completed, done, closed and paid jobs come in as completed history; scheduled ones with a date go on the schedule; the rest need scheduling. Cancelled jobs are skipped.",
    "Customers are matched to the ones you have. Anyone new is added with the job.",
  ],
  products: [
    "Your price list or a supplier's price sheet — a product name, the material and what it costs you per foot or per section.",
    "No cost column? A customer price per foot is worked back to a material cost at your markup.",
    "Tick “update products that already exist” to refresh costs on products with the same name.",
  ],
};

function ImportPanel({ kind, store, go, sync, undo, busy }: any) {
  const role = roleOf(store);
  const costs = can(role, "money.cost");
  const fields = fieldsFor(kind).filter((f: any) => costs || !f.money || kind === "products");
  const [file, setFile] = React.useState<any>(null);          // { name, size, headers, rows, sample }
  const [map, setMap] = React.useState<any>({});
  const [autoKeys, setAutoKeys] = React.useState<any>({});
  const [err, setErr] = React.useState<any>("");
  const [loading, setLoading] = React.useState<any>(false);
  const [drag, setDrag] = React.useState<any>(false);
  const [fillBlanks, setFillBlanks] = React.useState<any>(true);
  const [updateExisting, setUpdateExisting] = React.useState<any>(false);
  const [problems, setProblems] = React.useState<any>(false);
  const [allFields, setAllFields] = React.useState<any>(false); // the fields nothing matched stay folded until asked for
  const [result, setResult] = React.useState<any>(null);      // { batchId, file }
  const [cloud, setCloud] = React.useState<any>("pending");
  const inputRef = React.useRef<any>(null);
  const batchRef = React.useRef<any>(null);                   // one batch id per loaded file
  const phone = useNarrowScreen(860);                          // the import bar only rides along on a desktop

  // after an import: follow the save that carries it (a "saved" only counts
  // once a "saving" has started — the pill may already read saved from before)
  React.useEffect(() => {
    if (!result || !cloudOn()) return;
    const DB: any = window.VFP_DB;
    let saw = false;
    const off = DB.onStatus((st: string) => {
      if (st === "saving") { saw = true; setCloud((c: string) => (c === "saved" ? c : "saving")); }
      else if (st === "saved" && saw) setCloud("saved");
      else if ((st === "error" || st === "stale" || st === "guard") && saw) setCloud((c: string) => (c === "saved" ? c : "error"));
    });
    return () => { try { off && off(); } catch (e) {} };
  }, [result && result.batchId]);

  function load(name: string, text: string, size: number, sample?: boolean) {
    const { headers, rows } = csvRecords(text);
    if (!headers.length || !rows.length) { setErr("No rows found in that file — is the first line the column names?"); return; }
    if (rows.length > IMPORT_MAX_ROWS) { setErr(`That file has ${rows.length.toLocaleString()} rows — the limit is ${IMPORT_MAX_ROWS.toLocaleString()} per import. Split it (by year works well) and import each part.`); return; }
    const m = autoMap(kind, headers);
    if (!costs) fieldsFor(kind).forEach((f: any) => { if (f.money && kind !== "products") delete m[f.key]; });
    batchRef.current = newId("imp-");
    setErr(""); setResult(null); setProblems(false); setAllFields(false);
    setMap(m); setAutoKeys(Object.fromEntries(Object.keys(m).map((k) => [k, m[k]])));
    setFile({ name, size, headers, rows, sample: !!sample });
  }
  async function pick(f: File | null) {
    if (!f) return;
    setErr("");
    if (/\.(xlsx|xlsm|xls|numbers|ods)$/i.test(f.name)) { setErr("That's a spreadsheet workbook, not a CSV. Open it in Excel or Google Sheets, choose File → Save As (or Download) → CSV, and drop that file here."); return; }
    if (f.size > IMPORT_MAX_BYTES) { setErr(`That file is ${(f.size / 1048576).toFixed(1)} MB — the limit is ${IMPORT_MAX_BYTES / 1048576} MB. Split it into smaller files and import each one.`); return; }
    setLoading(true);
    try { load(f.name, await readFileText(f), f.size); }
    catch (e: any) { setErr("Couldn't read that file: " + String((e && e.message) || e).slice(0, 120)); }
    finally { setLoading(false); if (inputRef.current) inputRef.current.value = ""; }
  }
  function loadSample() {
    const text = kind === "jobs" ? sampleJobsCsv() : sampleCustomersCsv();
    load(kind === "jobs" ? "sample-job-history.csv" : "sample-client-list.csv", text, text.length, true);
  }
  function template() {
    const t = templateRows(kind);
    downloadCsv(`fenceworks-${kind}-template.csv`, toCsv(t.columns, t.rows));
  }
  function setField(key: string, header: string) {
    setMap((m: any) => {
      const next: any = { ...m };
      if (header) Object.keys(next).forEach((k) => { if (next[k] === header && k !== key) delete next[k]; });
      if (header) next[key] = header; else delete next[key];
      return next;
    });
  }

  // the engine's numbers for the price book: markup, labor per foot by material, section lengths
  const S = Object.assign({}, window.VFP.DEFAULT_SETTINGS, store.settings || {});
  const crew = (S.crews || []).find((c: any) => c.id === S.activeCrewId) || (S.crews || [])[0];
  const crewRate = crew ? (window.VFP.crewLoadedRate(crew) || (Number(crew.size) || 2) * (Number(crew.wage) || 28)) : 56;
  const plan = React.useMemo(() => {
    if (!file || result) return null;
    const ctx: any = { now: Date.now(), batchId: batchRef.current, newId };
    if (kind === "jobs") return planJobs(file.rows, map, { ...ctx, customers: store.customers || [], workOrders: store.workOrders || [], activeCrewId: S.activeCrewId || null, costs });
    if (kind === "products") {
      const planObj = window.VFP_PLAN ? window.VFP_PLAN.get(store) : null;
      const limit = planObj && planObj.caps ? planObj.caps.products : Infinity;
      return planProducts(file.rows, map, { ...ctx, allProducts: window.VFP.PRODUCTS || [], customProducts: store.customProducts || [], updateExisting, limit,
        markup: Number(S.markup) || 2, markupBase: S.markupBase,
        laborPerFtFor: (cat: string) => ((S.laborHours && S.laborHours[cat] != null ? Number(S.laborHours[cat]) : Number(S.laborHoursDefault) || 10) * crewRate) / 300,
        panelLenFor: (cat: string) => ((CATS as any)[cat] || (CATS as any).Wood).panelLen, colorFor: (cat: string) => (CAT_COLOR as any)[cat] || "#9aa6b8" });
    }
    return planCustomers(file.rows, map, { ...ctx, customers: store.customers || [], fillBlanks });
  }, [file, map, fillBlanks, updateExisting, result, store.customers, store.workOrders, store.customProducts]);

  function runImport() {
    if (!plan) return;
    const meta = { file: file.name, counts: plan.counts, sample: !!file.sample, at: Date.now() };
    store.importBatch(batchFromPlan(plan, { sample: file.sample }), meta);
    setCloud("pending");
    setResult({ batchId: plan.batchId, file: file.name });
  }
  function reset() { setFile(null); setResult(null); setMap({}); setErr(""); }

  const named = kind === "products" ? !!map.name : !!(map.name || map.firstName || map.lastName || (kind === "customers" && map.company));

  /* ---- after the import: what happened, and the way back ---- */
  if (result) {
    const h = (((store.settings && store.settings.importHistory) || []) as any[]).find((x: any) => x && x.id === result.batchId) || {};
    const left = importLeft(store, result.batchId);
    const wo = window.VFP_PLAN ? window.VFP_PLAN.allows(store, "workorders") : true;
    const openTo = kind === "jobs" ? (wo ? ["workorders", "Open Work Orders"] : ["customers", "Open Customers"]) : kind === "products" ? ["products", "Open Products"] : ["customers", "Open Customers"];
    const cloudLine = DEMO_ON() ? "Sandbox — nothing here leaves this tab."
      : !cloudOn() ? "Saved on this device."
      : cloud === "saved" ? "Saved to the cloud."
      : cloud === "error" ? "It hasn't all reached the cloud yet — hover the red dot at the top for why. It retries on your next change."
      : "Saving to the cloud… keep this tab open until the dot at the top turns green.";
    return (
      <div className="card pad" style={{ marginBottom: 18 }}>
        {h.undoneAt && !(busy && busy.id === result.batchId)
          ? <>
              <div className="row gap-8" style={{ alignItems: "center", marginBottom: 6 }}><Icon name="undo" size={18} style={{ color: "var(--fg-3)" }} /><h3 className="display" style={{ margin: 0, fontSize: 19 }}>Import undone</h3></div>
              <p className="muted" style={{ margin: "0 0 14px" }}>Everything {result.file} added is gone, and the blanks it filled in are empty again — except any you'd edited since.</p>
            </>
          : <>
              <div className="row gap-8" style={{ alignItems: "center", marginBottom: 6 }}><Icon name="check" size={18} style={{ color: "var(--green)" }} /><h3 className="display" style={{ margin: 0, fontSize: 19 }}>
                Imported {kind === "jobs" ? plural(h.jobs || 0, "jobs") : kind === "products" ? plural(h.products || 0, "products") : plural(h.customers || 0, "customers")}
              </h3></div>
              <p className="muted" style={{ margin: "0 0 4px" }}>
                {[kind === "jobs" && h.customers ? `${plural(h.customers, "customers")} added with them` : "",
                  h.updated ? `${h.updated} existing ${kind === "products" ? "product" : "customer"}${h.updated === 1 ? "" : "s"} updated` : "",
                  h.skipped ? `${h.skipped} row${h.skipped === 1 ? "" : "s"} skipped` : ""].filter(Boolean).join(" · ") || `From ${result.file}.`}
              </p>
              <p className="faint" style={{ margin: "0 0 14px", fontSize: 12.5 }}>{cloudLine}</p>
              {kind === "jobs" && !wo && <p className="faint" style={{ margin: "-6px 0 14px", fontSize: 12.5 }}>Work Orders and the schedule are on Pro. The jobs are saved and show on each customer's profile.</p>}
            </>}
        <div className="row gap-8" style={{ flexWrap: "wrap" }}>
          {!h.undoneAt && left.total > 0 && <button className="btn btn-primary" onClick={() => go(openTo[0])}><Icon name="chevright" size={14} />{openTo[1]}</button>}
          {!h.undoneAt && left.total > 0 && <button className="btn btn-outline" disabled={!!busy} onClick={() => undo(h.id ? h : { id: result.batchId, file: result.file })}><Icon name="undo" size={14} />Undo this import</button>}
          <button className="btn btn-ghost" onClick={reset}><Icon name="plus" size={14} />Import another file</button>
        </div>
      </div>
    );
  }

  /* ---- step 1: the file ---- */
  if (!file) {
    return (
      <div className="card pad" style={{ marginBottom: 18 }}>
        <div
          onDragOver={(e: any) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e: any) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]); }}
          style={{ border: "2px dashed " + (drag ? "var(--teal)" : "var(--line-2)"), background: drag ? "var(--teal-tint)" : "var(--surface-2)", borderRadius: 14, padding: "34px 18px", textAlign: "center", transition: ".15s" }}>
          <Icon name="download" size={26} style={{ color: "var(--teal)", transform: "rotate(180deg)" }} />
          <div style={{ fontWeight: 700, fontSize: 16, margin: "10px 0 4px" }}>{loading ? "Reading the file…" : `Drop your ${kind === "products" ? "price list" : kind === "jobs" ? "job history" : "customer list"} here`}</div>
          <div className="muted" style={{ fontSize: 13, marginBottom: 14 }}>CSV or TSV, up to {IMPORT_MAX_BYTES / 1048576} MB and {IMPORT_MAX_ROWS.toLocaleString()} rows. Nothing is saved until you've seen the preview.</div>
          <div className="row gap-8" style={{ justifyContent: "center", flexWrap: "wrap" }}>
            <button className="btn btn-primary" disabled={loading} onClick={() => inputRef.current && inputRef.current.click()}><Icon name="plus" size={14} />Choose a file</button>
            {DEMO_ON() && kind !== "products" && <button className="btn btn-outline" onClick={loadSample}><Icon name="bolt" size={14} />Try it with a sample file</button>}
            <button className="btn btn-ghost" onClick={template}><Icon name="download" size={14} />Download a template</button>
          </div>
          <input ref={inputRef} type="file" accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values" style={{ display: "none" }} aria-label="Choose a CSV file"
            onChange={(e: any) => pick(e.target.files && e.target.files[0])} />
        </div>
        {err && <p role="alert" style={{ color: "var(--red)", fontSize: 13.5, margin: "12px 0 0" }}><Icon name="x" size={13} style={{ verticalAlign: -2 }} /> {err}</p>}
        <ul className="muted" style={{ fontSize: 13, margin: "16px 0 0", paddingLeft: 18, lineHeight: 1.6 }}>
          {HELP[kind].map((x, i) => <li key={i}>{x}</li>)}
        </ul>
      </div>
    );
  }

  /* ---- step 2 + 3: the column matches, the preview, import ---- */
  const c = plan ? plan.counts : { create: 0, update: 0, skip: 0, error: 0, limit: 0, linkNew: 0 };
  const used = new Set(Object.values(map));
  // what the file matched on load (and the name, always) shows; the rest folds
  const shownFields = allFields ? fields : fields.filter((f: any) => map[f.key] || autoKeys[f.key] || f.key === "name");
  const foldedCount = fields.length - shownFields.length;
  const unused = file.headers.filter((h: string) => !used.has(h));
  const sampleOf = (h: string) => { const r = file.rows.find((x: any) => String(x[h] || "").trim()); return r ? String(r[h]).trim() : ""; };
  const probCount = plan ? plan.rows.filter((r: any) => r.action === "error" || r.action === "skip" || r.warnings.length).length : 0;
  const rows = plan ? previewRows(plan, problems) : [];
  const doing = c.create + c.update;
  const planObj = window.VFP_PLAN ? window.VFP_PLAN.get(store) : null;
  const importLabel = kind === "customers" ? `Import ${plural(c.create, "customers")}${c.update ? ` · fill in ${c.update}` : ""}`
    : kind === "jobs" ? `Import ${plural(c.create, "jobs")}`
    : `Import ${plural(c.create, "products")}${c.update ? ` · update ${c.update}` : ""}`;
  const big = file.rows.length > 1000 && cloudOn();
  // a tab that hasn't loaded the cloud copy (or lost it) doesn't save — don't import into it
  const held = cloudOn() && (sync === "error" || sync === "loading" || sync === "nosession")
    ? (sync === "nosession" ? "You're signed out — sign in again before importing, or nothing will save." : sync === "loading" ? "Still loading your workspace from the cloud — import in a moment." : "The cloud can't be reached, so this tab is read-only. Import when the dot at the top is green again.")
    : "";

  return (
    <>
      <div className="card pad" style={{ marginBottom: 14 }}>
        <div className="row between" style={{ flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div className="kicker kicker-teal" style={{ marginBottom: 4 }}><Icon name="clipboard" size={14} />Match your columns</div>
            <div style={{ fontWeight: 700, fontSize: 15, overflow: "hidden", textOverflow: "ellipsis" }}>{file.name}{file.sample ? " (sample)" : ""}</div>
            <div className="muted" style={{ fontSize: 12.5 }}>{file.rows.length.toLocaleString()} rows · {file.headers.length} columns · {Object.keys(map).length} matched</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={reset}><Icon name="x" size={13} />Choose a different file</button>
        </div>
        {!named && <p role="alert" style={{ color: "var(--red)", fontSize: 13, margin: "0 0 12px" }}>Pick the column that has the {kind === "products" ? "product name" : kind === "jobs" ? "customer's name" : "customer's name (or first and last name)"}.</p>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: "12px 16px" }}>
          {shownFields.map((f: any) => {
            const h = map[f.key] || "";
            const s = h ? sampleOf(h) : "";
            return (
              <label key={f.key} style={{ display: "grid", gap: 4, minWidth: 0 }}>
                <span className="field-label" style={{ margin: 0 }} title={f.hint || ""}>{f.label}{h && autoKeys[f.key] === h ? <span className="faint" style={{ fontWeight: 500, marginLeft: 6 }}>· matched</span> : null}</span>
                <select className="select" value={h} onChange={(e: any) => setField(f.key, e.target.value)} style={{ fontSize: 13, padding: "8px 34px 8px 10px" }}>
                  <option value="">— Not in this file —</option>
                  {file.headers.map((x: string) => <option key={x} value={x}>{x}</option>)}
                </select>
                <span className="faint" style={{ fontSize: 11.5, minHeight: 15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={s || f.hint || ""}>{h ? (s ? "e.g. " + s : "empty in this file") : (f.hint || "")}</span>
              </label>
            );
          })}
        </div>
        {(foldedCount > 0 || allFields) && (
          <button className="btn btn-ghost btn-sm" style={{ marginTop: 10 }} onClick={() => setAllFields((v: any) => !v)}>
            <Icon name="chevdown" size={13} style={{ transform: allFields ? "rotate(180deg)" : "none" }} />{allFields ? "Show only the matched fields" : `Match more columns · ${foldedCount} other field${foldedCount === 1 ? "" : "s"}`}
          </button>
        )}
        {unused.length > 0 && <p className="faint" style={{ fontSize: 12, margin: "12px 0 0" }}>Not used: {unused.slice(0, 30).join(", ")}{unused.length > 30 ? ` and ${unused.length - 30} more` : ""}.</p>}
        {kind === "customers" && (
          <label className="row gap-8" style={{ marginTop: 14, fontSize: 13.5, alignItems: "flex-start", cursor: "pointer" }}>
            <input type="checkbox" checked={fillBlanks} onChange={(e: any) => setFillBlanks(e.target.checked)} style={{ marginTop: 3 }} />
            <span><b>Fill in blanks on customers you already have.</b> <span className="muted">Matched by email, then phone, then name and street. Only empty fields are filled — nothing is overwritten, and Undo clears them again.</span></span>
          </label>
        )}
        {kind === "products" && (
          <label className="row gap-8" style={{ marginTop: 14, fontSize: 13.5, alignItems: "flex-start", cursor: "pointer" }}>
            <input type="checkbox" checked={updateExisting} onChange={(e: any) => setUpdateExisting(e.target.checked)} style={{ marginTop: 3 }} />
            <span><b>Update products that already exist</b> <span className="muted">(same name) with the costs in this file — for a new supplier price sheet. Undo puts the old numbers back.</span></span>
          </label>
        )}
        {kind === "jobs" && !map.total && <p className="faint" style={{ fontSize: 12.5, margin: "12px 0 0" }}>No contract value column matched — the jobs come in at $0 and reports won't count them as revenue.</p>}
      </div>

      {plan && (
        <div className="card pad" style={{ marginBottom: 14 }}>
          <div className="row between" style={{ flexWrap: "wrap", gap: 10, marginBottom: 10 }}>
            <div>
              <div className="kicker" style={{ marginBottom: 6 }}>Preview</div>
              <div className="row gap-8" style={{ flexWrap: "wrap" }}>
                <span className="chip teal">{c.create} new</span>
                {(c.update > 0 || kind !== "jobs") && <span className="chip violet">{c.update} to {kind === "customers" ? "fill in" : "update"}</span>}
                <span className="chip amber">{c.skip} skipped</span>
                {c.error > 0 && <span className="chip" style={{ color: "var(--red)", borderColor: "var(--red-tint)", background: "var(--red-tint)" }}>{c.error} with errors</span>}
                {kind === "jobs" && c.linkNew > 0 && <span className="chip">{plural(c.linkNew, "customers")} added with them</span>}
              </div>
            </div>
            <div className="seg" role="tablist" aria-label="Which rows">
              <button role="tab" aria-selected={!problems} className={"seg-btn" + (!problems ? " on" : "")} onClick={() => setProblems(false)}>First {Math.min(PREVIEW_ROWS, plan.rows.length)} rows</button>
              <button role="tab" aria-selected={problems} className={"seg-btn" + (problems ? " on" : "")} onClick={() => setProblems(true)}>Skipped &amp; warnings · {probCount}</button>
            </div>
          </div>
          {kind === "products" && c.limit > 0 && (
            <p style={{ fontSize: 13, margin: "0 0 10px", color: "var(--amber)" }}>
              {c.limit} product{c.limit === 1 ? "" : "s"} won't fit: {planObj && Number.isFinite(planObj.caps && planObj.caps.products) && planObj.caps.products < PRICEBOOK_MAX ? `the ${planObj.name} plan holds ${planObj.caps.products} of your own products.` : `imports stop at ${PRICEBOOK_MAX} products in the price book.`}
              {planObj && Number.isFinite(planObj.caps && planObj.caps.products) && planObj.caps.products < PRICEBOOK_MAX && <> <a style={{ color: "var(--teal)", cursor: "pointer" }} onClick={() => go("account")}>See plans →</a></>}
            </p>
          )}
          <PreviewTable kind={kind} rows={rows} store={store} />
          {problems && probCount > rows.length && <p className="faint" style={{ fontSize: 12, margin: "8px 0 0" }}>Showing the first {rows.length} of {probCount}.</p>}
        </div>
      )}

      <div className="card pad row between" style={{ marginBottom: 18, flexWrap: "wrap", gap: 12, ...(phone ? {} : { position: "sticky", bottom: 12, zIndex: 3, boxShadow: "var(--shadow-lg)" }) }}>
        <div className="muted" style={{ fontSize: 13, maxWidth: 560 }}>
          {doing ? <>Each record is marked with this import, so <b style={{ color: "var(--fg-2)" }}>Undo</b> takes back exactly what it adds and fills in.</> : "Nothing to import with these matches."}
          {big && <> Large imports take a minute to reach the cloud — keep this tab open until the dot at the top turns green.</>}
          {held && <div role="alert" style={{ color: "var(--red)", marginTop: 4 }}>{held}</div>}
        </div>
        <button className="btn btn-primary" disabled={!plan || !doing || !named || !!held} onClick={runImport}><Icon name="check" size={15} />{importLabel}</button>
      </div>
    </>
  );
}

function ResultCell({ r }: any) {
  const tone: any = {
    create: ["New", "teal"], update: ["Update", "violet"], skip: ["Skip", "amber"], error: ["Error", ""],
  }[r.action] || ["", ""];
  const line = (x: string, i: number, color?: string) => <div key={i} style={{ fontSize: 11.5, lineHeight: 1.35, marginTop: 3, color }}>{x}</div>;
  return (
    <div style={{ minWidth: 150, maxWidth: 360, whiteSpace: "normal" }}>
      <span className={"chip " + tone[1]} style={{ fontSize: 10.5, padding: "1px 8px", ...(r.action === "error" ? { color: "var(--red)", borderColor: "var(--red-tint)", background: "var(--red-tint)" } : {}) }}>{tone[0]}</span>
      {r.note ? line(r.note, -1, "var(--fg-2)") : null}
      {r.errors.map((x: string, i: number) => line(x, i, "var(--red)"))}
      {r.warnings.map((x: string, i: number) => line(x, 100 + i, "var(--amber)"))}
    </div>
  );
}

function PreviewTable({ kind, rows, store }: any) {
  // on a phone the table would put the result off to the right; each row
  // becomes a short card instead — what it is, then what happens to it
  const narrow = useNarrowScreen(700);
  if (narrow) {
    if (!rows.length) return <p className="muted" style={{ fontSize: 13, padding: "10px 2px" }}>No rows to show.</p>;
    return (
      <div style={{ display: "grid", gap: 8 }}>
        {rows.map((r: any) => {
          const v = r.view || {};
          const title = kind === "products" ? v.name : v.name || "(no name)";
          const bits = kind === "jobs" ? [v.title && v.title !== title ? v.title.replace(title + " — ", "") : "", v.status ? STAGE_LABEL[v.status] || v.status : "", v.total != null ? DIO_money0(v.total) : ""]
            : kind === "products" ? [v.category, r.rec ? DIO_money(r.rec.panelCost) + " / " + r.rec.panelWidth + " ft" : ""]
            : [v.phone, v.email, v.address];
          return (
            <div key={r.line} style={{ padding: "9px 11px", border: "1px solid var(--line)", borderRadius: 10, background: "var(--surface)" }}>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}><span className="faint tnum" style={{ fontWeight: 500, marginRight: 6 }}>{r.line}</span>{title}</div>
              {bits.filter(Boolean).length > 0 && <div className="muted" style={{ fontSize: 12, margin: "2px 0 5px", overflowWrap: "anywhere" }}>{bits.filter(Boolean).join(" · ")}</div>}
              <ResultCell r={r} />
            </div>
          );
        })}
      </div>
    );
  }
  const lineCol = { key: "line", label: "Row", num: true, width: 52, render: (r: any) => <span className="faint tnum">{r.line}</span> };
  const resultCol = { key: "result", label: "Result", sort: false, render: (r: any) => <ResultCell r={r} /> };
  const sub = (a: any, b: any) => <><div style={{ fontWeight: 600 }}>{a || <span className="faint">—</span>}</div>{b ? <div className="faint" style={{ fontSize: 11.5 }}>{b}</div> : null}</>;
  let cols: any[];
  if (kind === "jobs") cols = [lineCol,
    { key: "customer", label: "Customer", render: (r: any) => sub(r.view.name, r.newCustomer ? "new customer" : r.match ? "linked · " + r.match.by : "") },
    { key: "job", label: "Job", className: "wrap", render: (r: any) => <span style={{ whiteSpace: "normal" }}>{r.view.title}</span> },
    { key: "status", label: "Status", render: (r: any) => sub(r.view.status ? STAGE_LABEL[r.view.status] || r.view.status : "", r.view.done ? "done " + r.view.done : r.view.start ? "on " + r.view.start : "") },
    { key: "total", label: "Total", num: true, render: (r: any) => r.view.total != null ? DIO_money0(r.view.total) : <span className="faint">—</span> },
    resultCol];
  else if (kind === "products") cols = [lineCol,
    { key: "name", label: "Product", render: (r: any) => sub(r.view.name, r.view.category + (r.view.style ? " · " + r.view.style : "")) },
    { key: "cost", label: "Material", num: true, render: (r: any) => { const p = r.rec; return p ? sub(DIO_money(p.panelCost) + " / " + p.panelWidth + " ft", DIO_money(round2(p.panelCost / (p.panelWidth || 1))) + "/ft") : <span className="faint">—</span>; } },
    { key: "gate", label: "Gate", num: true, render: (r: any) => r.rec && Number(r.rec.gateCost) > 0 ? DIO_money0(r.rec.gateCost) : <span className="faint">—</span> },
    { key: "installed", label: "Installed $/ft", num: true, title: "Customer price per foot on the shared sample job, with your markup, crew rate and tax", render: (r: any) => {
      if (!r.rec) return <span className="faint">—</span>;
      const j = sampleJob(r.rec, store.settings);
      return j.ok ? <b style={{ color: "var(--teal)" }}>{DIO_money(j.perFoot)}</b> : <span className="faint">—</span>;
    } },
    resultCol];
  else cols = [lineCol,
    { key: "name", label: "Name", render: (r: any) => sub(r.view.name, r.view.company) },
    { key: "contact", label: "Phone · email", render: (r: any) => sub(r.view.phone, r.view.email) },
    { key: "address", label: "Address", className: "wrap", render: (r: any) => <span style={{ whiteSpace: "normal" }}>{r.view.address || <span className="faint">—</span>}</span> },
    { key: "tags", label: "Tags · source", render: (r: any) => sub((r.view.tags || []).join(", "), r.view.source ? (r.view.sourceDetail || SOURCE_LABELS[r.view.source]) : "") },
    resultCol];
  return <DataTable dense sort={false} columns={cols} rows={rows} rowKey="line"
    empty={<p className="muted" style={{ fontSize: 13, padding: "10px 2px" }}>No rows to show.</p>} />;
}

function UndoProgress({ busy }: any) {
  const pct = busy.total ? Math.round((busy.done / busy.total) * 100) : 0;
  return (
    <div style={{ margin: "0 0 14px" }}>
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 5 }}>Undoing… {busy.done.toLocaleString()} of {busy.total.toLocaleString()} removed{cloudOn() ? " — each part waits for its cloud save" : ""}</div>
      <div style={{ height: 6, borderRadius: 99, background: "var(--line-2)", overflow: "hidden" }}><div style={{ width: pct + "%", height: "100%", background: "var(--teal)", transition: "width .3s" }} /></div>
    </div>
  );
}

/* the last imports (settings.importHistory), each with its own Undo */
function HistoryCard({ store, undo, busy }: any) {
  const hist = (((store.settings && store.settings.importHistory) || []) as any[]).filter((h: any) => h && (!h.sample || DEMO_ON()));
  if (!hist.length) return null;
  const kindIcon: any = { customers: "user", jobs: "clipboard", products: "box" };
  return (
    <div className="card pad">
      <div className="kicker" style={{ marginBottom: 10 }}>Recent imports</div>
      <div style={{ display: "grid", gap: 8 }}>
        {hist.map((h: any) => {
          const left = importLeft(store, h.id);
          const what = [h.customers ? plural(h.customers, "customers") : "", h.jobs ? plural(h.jobs, "jobs") : "", h.products ? plural(h.products, "products") : "", h.updated ? `${h.updated} updated` : "", h.skipped ? `${h.skipped} skipped` : ""].filter(Boolean).join(" · ") || "nothing added";
          return (
            <div key={h.id} style={{ padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 10, background: "var(--surface)" }}>
              <div className="row between" style={{ gap: 10, flexWrap: "wrap" }}>
                <div className="row gap-8" style={{ minWidth: 0, flex: "1 1 220px", alignItems: "flex-start", flexWrap: "nowrap" }}>
                  <Icon name={kindIcon[h.kind] || "download"} size={15} style={{ color: "var(--fg-3)", marginTop: 2, flexShrink: 0 }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 13.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.file || "Import"}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{new Date(h.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} · {what}</div>
                  </div>
                </div>
                {h.undoneAt
                  ? <span className="chip">Undone</span>
                  : left.total > 0
                    ? <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={() => undo(h)}><Icon name="undo" size={13} />{busy && busy.id === h.id ? "Undoing…" : "Undo"}</button>
                    : <span className="faint" style={{ fontSize: 12 }}>Nothing left to undo</span>}
              </div>
              {busy && busy.id === h.id && <div style={{ marginTop: 10 }}><UndoProgress busy={busy} /></div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---- Export: every list, all plans ---- */
function ExportPanel({ store, canPrice, canJobs, canEst }: any) {
  const role = roleOf(store);
  const leads = new Set((store.quotes || []).filter((q: any) => q && q.customerId).map((q: any) => q.customerId));
  const cards = [
    { id: "customers", icon: "user", title: "Customers & leads", n: (store.customers || []).length, word: ["customer", "customers"],
      sub: "Name, phone, email, address, tags, lead source, customer since, jobs and lifetime value — plus whether they're a customer or still a lead.", show: true,
      extra: leads.size ? `${leads.size} have an estimate` : "" },
    { id: "estimates", icon: "send", title: "Estimates", n: (store.quotes || []).length, word: ["estimate", "estimates"],
      sub: "Number, customer, status, total, paid, and the created, sent and signed dates.", show: canEst },
    { id: "jobs", icon: "clipboard", title: "Jobs", n: (store.workOrders || []).length, word: ["job", "jobs"],
      sub: can(role, "money.cost") ? "Every work order with its status, dates, total and crew — and labor, material and hours where they're known." : "Every work order with its status, dates, total and crew.", show: canJobs },
    { id: "products", icon: "box", title: "Products", n: (window.VFP.PRODUCTS || []).length, word: ["product", "products"],
      sub: "Your price book: material cost per section and per foot, post and gate costs, and the installed price per foot on the sample job.", show: canPrice },
  ].filter((c) => c.show);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 14, marginBottom: 18 }}>
      {cards.map((c) => (
        <div key={c.id} className="card pad" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div className="row gap-8" style={{ alignItems: "center" }}><Icon name={c.icon} size={17} style={{ color: "var(--teal)" }} /><b style={{ fontSize: 15 }}>{c.title}</b></div>
          <div className="display tnum" style={{ fontSize: 26 }}>{c.n.toLocaleString()} <span className="muted" style={{ fontSize: 13, fontWeight: 500 }}>{c.word[c.n === 1 ? 0 : 1]}{c.extra ? " · " + c.extra : ""}</span></div>
          <p className="muted" style={{ fontSize: 12.5, margin: 0, flex: 1 }}>{c.sub}</p>
          <button className="btn btn-outline btn-sm" disabled={!c.n} onClick={() => downloadExport(c.id, store)}><Icon name="download" size={13} />Download CSV</button>
        </div>
      ))}
    </div>
  );
}

Object.assign(window, { DataIOPage });

export { DataIOPage, downloadExport };
